import { createContext, isValidElement, useContext, useEffect, useState, type ReactNode } from "react";
import clsx from "clsx";
import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Loader2, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { errorMessage } from "@/shared/i18n";
import { DemoButton } from "./DemoButton";

/**
 * Where the shell says a page is: its area's name, shown over the page's title, and on a
 * phone (where there's no side menu) a way to the area's other pages.
 */
export const PlaceContext = createContext<{ area?: string; onArea?: () => void }>({});

export function PageHeader(props: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; back?: { to: string; search?: Record<string, string>; label: ReactNode } }) {
  const place = useContext(PlaceContext);
  return (
    <div className="mb-6 flex flex-wrap items-end gap-3">
      {/* On a phone the title gets the whole line and the buttons go under it. */}
      <div className="min-w-0 flex-1 max-sm:basis-full">
        {props.back ? (
          <Link to={props.back.to} search={props.back.search as never} className="mb-1 inline-flex items-center gap-1 text-[13px] text-ink-3 hover:text-ink">
            <ChevronRight className="size-3.5 ltr:rotate-180" />
            {props.back.label}
          </Link>
        ) : place.area && (place.onArea ? (
          <button type="button" onClick={place.onArea} className="mb-1 inline-flex items-center gap-1 text-[13px] text-ink-3 hover:text-ink lg:pointer-events-none">
            {place.area}<ChevronDown className="size-3.5 lg:hidden" aria-hidden />
          </button>
        ) : <div className="mb-1 text-[13px] text-ink-3">{place.area}</div>)}
        <h1 className="text-[26px] leading-[1.2] font-bold tracking-[-0.01em] break-words sm:truncate sm:text-[30px]">{props.title}</h1>
        {props.subtitle && <div className="mt-1.5 text-ink-2">{props.subtitle}</div>}
      </div>
      {props.actions && <div className="flex flex-wrap items-center gap-2">{props.actions}</div>}
    </div>
  );
}

/** Shown only once a load has taken 300ms: a quick load shows nothing rather than a flash. */
export function Spinner({ label }: { label?: string }) {
  const { t } = useTranslation();
  const [late, setLate] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setLate(true), 300);
    return () => clearTimeout(id);
  }, []);
  if (!late) return <div className="py-10" aria-busy="true" />;
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-ink-3" role="status">
      <Loader2 className="size-4 animate-spin" />
      {label ?? t("common.loading")}
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-2 rounded-[var(--radius-ctl)] border border-danger/25 bg-danger-soft px-3 py-2.5 text-danger" role="alert">
      <AlertCircle className="mt-0.5 size-4 shrink-0" />
      <div className="flex-1">{errorMessage(error)}</div>
      {onRetry && (
        <button type="button" className="text-[13px] font-medium underline" onClick={onRetry}>{t("common.retry")}</button>
      )}
    </div>
  );
}

export function Empty({ title, action }: { title: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-9 text-center text-ink-2">
      <p>{title}</p>
      {action}
    </div>
  );
}

/**
 * A list with nothing in it yet: what goes here and what it's for, the way to add the
 * first one (children: a button, a spreadsheet import), and the demo company to see the
 * page full.
 */
export function FirstUse({ title, hint, children }: { title: ReactNode; hint?: ReactNode; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-2 px-4 py-12 text-center">
      <p className="text-[16px] font-semibold">{title}</p>
      {hint && <p className="text-ink-2">{hint}</p>}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        {children}
        <DemoButton className="text-ledger" label={t("demo.seeFull")} />
      </div>
    </div>
  );
}

/**
 * A full-page form's buttons, held at the bottom of the screen so saving never needs a
 * scroll back up. hint says what's missing while the save button is off.
 */
export function FormBar({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="sticky bottom-[var(--bottom-bar)] z-10 -mx-4 mt-5 -mb-5 flex items-center justify-end gap-3 border-t border-line bg-surface/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10">
      {hint && <span className="me-auto text-[13px] text-ink-3">{hint}</span>}
      {children}
    </div>
  );
}

/** A part of a page under its heading: set apart by space and its title, not by a box. */
/** A part of a page under its heading; with no heading (the page's title says it) only its actions, at the end. */
export function Section({ title, actions, children }: { title?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="min-w-0">
      {(title || actions) && (
        <div className={clsx("mb-3 flex min-h-8 flex-wrap items-center gap-x-3 gap-y-1", title ? "justify-between" : "justify-end")}>
          {title && <h2 className="text-[17px] font-bold">{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/** Whether a value says nothing: nothing, a blank, or the dash a page puts where a field has no value. */
export function isEmpty(v: ReactNode): boolean {
  if (v == null || v === false || v === "" || v === "—") return true;
  if (isValidElement<{ children?: ReactNode }>(v) && typeof v.type === "string") {
    const text = (n: ReactNode): string =>
      n == null || typeof n === "boolean" ? "" : Array.isArray(n) ? n.map(text).join("") : isValidElement<{ children?: ReactNode }>(n) ? text(n.props.children) : String(n);
    const t = text(v.props.children).trim();
    return t === "" || t === "—";
  }
  return false;
}

/**
 * A record's facts: each label over its value, in as many columns as the space has. Fields with
 * nothing in them wait behind one line ("show the 4 empty fields") instead of filling the page with dashes.
 */
export function DL({ items }: { items: [ReactNode, ReactNode][] }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const filled = items.filter(([, v]) => !isEmpty(v));
  const hidden = items.length - filled.length;
  const shown = all || filled.length === 0 ? items : filled;
  return (
    <div className="@container">
      <dl className="grid gap-x-8 gap-y-4 @md:grid-cols-2 @3xl:grid-cols-3">
        {shown.map(([k, v], i) => (
          <div key={i} className={clsx("min-w-0", typeof v === "string" && v.length > 70 && "@md:col-span-2")}>
            <dt className="text-[13px] text-ink-3">{k}</dt>
            <dd className="mt-0.5 break-words">{isEmpty(v) ? <span className="text-ink-3">—</span> : v}</dd>
          </div>
        ))}
      </dl>
      {hidden > 0 && filled.length > 0 && (
        <button type="button" className="mt-4 text-[13px] text-ledger hover:underline" onClick={() => setAll((x) => !x)}>
          {all ? t("common.hideEmpty") : t("common.showEmpty", { count: hidden })}
        </button>
      )}
    </div>
  );
}
